import * as React from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import NotInterestedIcon from "@mui/icons-material/NotInterested";
import ModuleSelect from "../components/ModuleSelect";
import { useTranslation } from "react-i18next";
import { sortRequirementsByWeight } from "../helpers/helpers";
import { CGI_BLACK, discreteDescriptions } from "../helpers/enums";
import {
  Box,
  Button,
  Checkbox,
  Container,
  MobileStepper,
  Slider,
  Typography,
} from "@mui/material";

export default function PublicAssessment({
  finishAssessment,
  assessmentData,
  assessmentAnswerData,
  visitedQuestions,
  setNewAssessmentAnswers,
  markQuestionAsVisited,
}) {
  const [selectedModule, setSelectedModule] = React.useState("");
  const { t } = useTranslation();
  //Stepper:
  const [activeStep, setActiveStep] = React.useState(0);

  React.useEffect(() => {
    setActiveStep(0);
  }, [selectedModule]);

  const questionArr =
    selectedModule === "" ? [] : Object.values(selectedModule.questions);

  React.useEffect(() => {
    if (questionArr.length && selectedModule !== "") {
      markQuestionAsVisited(questionArr[activeStep].id);
    }
  }, [activeStep, selectedModule]);

  const handleNext = () => {
    if (activeStep === questionArr.length - 1) {
      setSelectedModule("");
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const marks = [
    {
      value: 0,
      label: "0",
    },
    {
      value: 1,
      label: "1",
    },
    {
      value: 2,
      label: "2",
    },
    {
      value: 3,
      label: "3",
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        mt: 5,
        width: 1,
      }}
    >
      {selectedModule === "" ? (
        <ModuleSelect
          assessmentData={assessmentData}
          visitedQuestions={visitedQuestions}
          setSelectedModule={setSelectedModule}
          finishAssessment={finishAssessment}
        />
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              mt: { xs: 2, sm: -15 },
            }}
          >
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => setSelectedModule("")}
            >
              {t("backToModuleSelection")}{" "}
            </Button>
          </Box>
          <Typography
            sx={{ mt: 0.5, ml: 1, fontSize: 15, fontStyle: "italic", pr: 1 }}
          >
            Assessment: {assessmentData.title}
          </Typography>
          <Typography
            sx={{ mt: 0.5, ml: 1, fontSize: 15, fontStyle: "italic", pr: 1 }}
          >
            Module: {selectedModule.title}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: 1,
              alignItems: "center",
              mt: 5,
            }}
          >
            <Container maxWidth={"sm"} sx={{ minHeight: "55vh" }}>
              <Typography sx={{ mx: 2, fontSize: 17 }}>
                {t("question")} {activeStep + 1}
                {" - "}
                {questionArr[activeStep].topic}
              </Typography>
              <Typography align="center" sx={{ fontFamily: "Inter" }}>
                {questionArr[activeStep].content}
              </Typography>
              <Typography
                sx={{
                  fontSize: 16,
                  mx: 3,
                  mt: 2,
                  textDecoration: "underline",
                  pr: 1,
                  fontStyle: "italic",
                }}
              >
                {t("requirements")}:
              </Typography>
              {Object.values(questionArr[activeStep].requirements)
                .sort(sortRequirementsByWeight)
                .map((requirement, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      width: 1,
                      mt: 1,
                      pt: 1,
                      borderTop: index === 0 ? 0 : 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        borderRight: 1,
                        pr: 2,
                      }}
                    >
                      <Slider
                        size="small"
                        sx={{
                          width: 9 / 10,
                          ml: 0.6,
                          mb: 1.25,
                          "& .MuiSlider-markLabel": {
                            marginTop: -1.2,
                            fontSize: 12,
                            userSelect: "none",
                          },
                        }}
                        valueLabelFormat={(value) =>
                          t(discreteDescriptions[value])
                        }
                        valueLabelDisplay="auto"
                        value={
                          assessmentAnswerData?.[requirement.id]?.score || 0
                        }
                        disabled={
                          assessmentAnswerData?.[requirement.id]?.disabled
                        }
                        step={1}
                        marks={marks}
                        min={0}
                        max={3}
                        onChange={(e) => {
                          setNewAssessmentAnswers(requirement.id, {
                            ...assessmentAnswerData?.[requirement.id],
                            score: e.target.value,
                          });
                        }}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          width: 150,
                        }}
                      >
                        <Typography
                          sx={{ fontSize: 12, userSelect: "none", mr: 0.5 }}
                        >
                          Irrelevant:
                        </Typography>
                        <Checkbox
                          size="small"
                          checked={
                            assessmentAnswerData?.[requirement.id]?.disabled ||
                            false
                          }
                          sx={{
                            color: "#dddddd",
                            "&.Mui-checked": {
                              color: "#000000",
                            },
                            height: 30,
                            width: 30,
                          }}
                          icon={<NotInterestedIcon />}
                          checkedIcon={<NotInterestedIcon />}
                          onClick={(e) =>
                            setNewAssessmentAnswers(requirement.id, {
                              ...assessmentAnswerData?.[requirement.id],
                              disabled: e.target.checked,
                            })
                          }
                        />
                      </Box>
                    </Box>
                    <Typography
                      sx={{
                        ml: 2,
                        fontFamily: "Inter",
                        color: assessmentAnswerData?.[requirement.id]?.disabled
                          ? "#aaa"
                          : CGI_BLACK,
                        my: 0.25,
                      }}
                    >
                      {requirement.text}
                    </Typography>
                  </Box>
                ))}
            </Container>
            <Typography
              sx={{
                mt: 5,
                fontSize: 17,
                mb: -2,
                zIndex: 100,
                userSelect: "none",
              }}
            >
              {t("question")} {activeStep + 1}/{questionArr.length}
            </Typography>
            <MobileStepper
              variant="progress"
              steps={questionArr.length}
              position="static"
              activeStep={activeStep}
              sx={{ width: 1, maxWidth: 850, flexGrow: 1 }}
              nextButton={
                <Button
                  size="small"
                  onClick={handleNext}
                  sx={{
                    width: 200,
                    borderTopRightRadius: 20,
                    borderBottomRightRadius: 20,
                  }}
                >
                  {activeStep === questionArr.length - 1
                    ? "To module selection"
                    : "Next"}
                  <KeyboardArrowRight />
                </Button>
              }
              backButton={
                <Button
                  size="small"
                  onClick={handleBack}
                  disabled={activeStep === 0}
                  sx={{
                    width: 200,
                    borderTopLeftRadius: 20,
                    borderBottomLeftRadius: 20,
                  }}
                >
                  <KeyboardArrowLeft />
                  {t("back")}
                </Button>
              }
            />
          </Box>
        </>
      )}
    </Box>
  );
}
